import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  useGetPropertyQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useGetUserFavouritePropertyQuery,
} from '../../features/api/apiSlice';
import { selectIsAuthenticated, selectCurrentUser } from '../../features/auth/authSlice';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import SEO from '../../components/seo';
import { useToast } from '../../components/ui/Toast';
import {
  getTransactionBadgeText,
  getTransactionLabel,
  TRANSACTION_BADGE_STYLE,
} from '../../lib/propertyLabels';
import { useCurrency } from '../../features/currency/CurrencyContext';
import { trackEvent } from '../../lib/analytics';

const findParameter = (parameters, name) => {
  const param = parameters?.find((p) => p.name?.toLowerCase() === name.toLowerCase());
  return param ? param.value : null;
};

const PropertyDetail = () => {
  const navigate = useNavigate();
  const { slug_id } = useParams();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authUser = useSelector(selectCurrentUser);
  const addToast = useToast();
  const { formatPrice, currency } = useCurrency();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: authUser?.name || '',
    email: authUser?.email || '',
    phone: authUser?.mobile || '',
    message: '',
  });

  const { data: property, isLoading, error } = useGetPropertyQuery(slug_id);
  useEffect(() => {
    if (property?.id) {
      trackEvent(
        'property_view',
        { property_id: property.id, slug: slug_id, title: property.title },
        'click'
      );
    }
  }, [property?.id, slug_id]);
  const { data: favourites = [] } = useGetUserFavouritePropertyQuery(undefined, {
    skip: !isAuthenticated,
  });
  const [addFavorite, { isLoading: addingFav }] = useAddFavoriteMutation();
  const [deleteFavorite, { isLoading: removingFav }] = useDeleteFavoriteMutation();

  const isSaved = useMemo(() => {
    if (!property?.id || !favourites?.length) return false;
    return favourites.some((f) => String(f.id || f.property_id) === String(property.id));
  }, [favourites, property?.id]);

  const galleryImages = useMemo(() => {
    if (!property) return [];
    if (property.gallery?.length) {
      return property.gallery.map((g) => g.image_url || g).filter(Boolean);
    }
    return property.title_image ? [property.title_image] : [];
  }, [property]);

  const bedrooms = findParameter(property?.parameters, 'bedrooms');
  const bathrooms = findParameter(property?.parameters, 'bathrooms');
  const area =
    findParameter(property?.parameters, 'area') || findParameter(property?.parameters, 'sqft');

  const isTaken = property?.is_premium;
  const statusText = isTaken
    ? 'Taken'
    : property?.status === '1' || property?.status === 1
      ? 'Available'
      : 'Unavailable';
  const statusBadgeClass = isTaken
    ? 'bg-rose-50 text-rose-700 border-rose-100'
    : property?.status === '1' || property?.status === 1
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : 'bg-amber-50 text-amber-800 border-amber-100';

  const typeLabel = getTransactionLabel(property?.property_type) || 'Listing';
  const typeBadge = getTransactionBadgeText(property?.property_type);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-slate-200 border-t-slate-900 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">Loading property…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !property) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-16 flex items-center justify-center min-h-[60vh] px-4">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 bg-white border border-slate-200 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <Icon name="Home" size={28} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Property not found</h3>
            <p className="text-slate-500 mb-6">
              This listing may have been removed or the link is incorrect.
            </p>
            <Button onClick={() => navigate('/property-search-browse')}>Back to search</Button>
          </div>
        </div>
      </div>
    );
  }

  const propertySchema = {
    '@context': 'https://schema.org',
    '@type':
      String(property?.property_type).toLowerCase() === 'rent'
        ? 'Apartment'
        : 'SingleFamilyResidence',
    name: property?.title,
    description:
      property?.description ||
      `View this ${property?.category?.category || 'property'} for ${typeLabel} in ${property?.address}.`,
    url: `https://hirebroker.co/property/${slug_id}`,
    image: property?.title_image || galleryImages[0],
    address: {
      '@type': 'PostalAddress',
      streetAddress: property?.address || '',
      addressLocality: property?.city || '',
      addressCountry: 'UG',
    },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'UGX',
      price: parseFloat(property?.price) || 0,
      availability: 'https://schema.org/InStock',
      url: `https://hirebroker.co/property/${slug_id}`,
    },
  };

  const nextImage = () => {
    if (galleryImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % galleryImages.length);
    }
  };
  const prevImage = () => {
    if (galleryImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
    }
  };

  const handleSaveProperty = async () => {
    if (!isAuthenticated) {
      addToast('Please sign in to save properties.', 'info');
      navigate('/login', { state: { from: { pathname: `/property/${slug_id}` } } });
      return;
    }
    try {
      if (isSaved) {
        await deleteFavorite(property.id).unwrap();
        addToast('Removed from saved properties.', 'info');
      } else {
        await addFavorite(property.id).unwrap();
        addToast('Property saved to your shortlist.', 'success');
      }
    } catch {
      addToast('Could not update saved list.', 'error');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: property.title, url });
      } catch {
        /* cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        addToast('Link copied to clipboard.', 'info');
      } catch {
        addToast('Could not copy link.', 'error');
      }
    }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Inquiry about ${property.title}`);
    const body = encodeURIComponent(
      `${contactForm.message}\n\n—\n${contactForm.name}\n${contactForm.email}\n${contactForm.phone || ''}`
    );
    if (property.email) {
      window.location.href = `mailto:${property.email}?subject=${subject}&body=${body}`;
    } else if (property.mobile) {
      window.open(`tel:${property.mobile}`);
    } else {
      addToast('No contact details available for this listing.', 'warning');
    }
    setShowContactForm(false);
  };

  const searchContext = {
    location: property?.city || property?.address || '',
    propertyType: property?.category?.category || '',
    priceRange: property?.price ? formatPrice(property.price, 'UGX') : '',
    queryString: '',
  };

  const locationLine = [property.address, property.city, property.state, property.country]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="min-h-screen bg-slate-50 overflow-x-hidden">
      <SEO
        title={property?.title}
        description={
          property?.description ||
          `Find details for ${property?.title} including price, location, and features.`
        }
        canonicalUrl={`https://hirebroker.co/property/${slug_id}`}
        ogImage={property?.title_image}
        schema={propertySchema}
      />
      <Header />

      <main className="pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Breadcrumbs searchContext={searchContext} currentProperty={property} />
        </div>

        {/* Gallery */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="relative w-full h-[360px] sm:h-[440px] md:h-[540px] rounded-3xl overflow-hidden bg-slate-900 shadow-xl border border-slate-800/20">
            {galleryImages.length ? (
              <>
                <Image
                  src={galleryImages[currentImageIndex]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/20 pointer-events-none" />

                {galleryImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={prevImage}
                      className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-white/15 hover:bg-white/25 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-all"
                      aria-label="Previous image"
                    >
                      <Icon name="ChevronLeft" size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={nextImage}
                      className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-12 sm:h-12 bg-white/15 hover:bg-white/25 text-white rounded-full flex items-center justify-center backdrop-blur-md border border-white/20 transition-all"
                      aria-label="Next image"
                    >
                      <Icon name="ChevronRight" size={20} />
                    </button>
                  </>
                )}

                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  <span
                    className="px-3 py-1.5 rounded-full text-xs font-semibold shadow"
                    style={TRANSACTION_BADGE_STYLE}
                  >
                    {typeBadge}
                  </span>
                  {property.category?.category && (
                    <span className="px-3 py-1.5 bg-slate-900/70 text-white rounded-full text-xs font-semibold backdrop-blur-sm">
                      {property.category.category}
                    </span>
                  )}
                  <span
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border backdrop-blur-sm ${statusBadgeClass}`}
                  >
                    {statusText}
                  </span>
                </div>

                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    type="button"
                    onClick={handleShare}
                    className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-md flex items-center justify-center"
                    aria-label="Share"
                  >
                    <Icon name="Share2" size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveProperty}
                    disabled={addingFav || removingFav}
                    className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white border border-white/20 backdrop-blur-md flex items-center justify-center"
                    aria-label="Save"
                  >
                    <Icon
                      name="Heart"
                      size={16}
                      className={isSaved ? 'text-rose-400 fill-rose-400' : 'text-white'}
                    />
                  </button>
                </div>

                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-white text-lg sm:text-2xl font-bold drop-shadow-sm truncate">
                      {property.title}
                    </p>
                    <p className="text-white/85 text-sm flex items-center gap-1.5 mt-1 truncate">
                      <Icon name="MapPin" size={14} />
                      {locationLine}
                    </p>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-white text-xl sm:text-2xl font-bold drop-shadow-sm">
                      {formatPrice(property.price, 'UGX')}
                    </p>
                    <p className="text-white/80 text-xs mt-0.5">
                      {currentImageIndex + 1} / {galleryImages.length} photos
                    </p>
                  </div>
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Icon name="ImageOff" size={40} className="mx-auto mb-2" />
                  <p>No images available</p>
                </div>
              </div>
            )}
          </div>

          {galleryImages.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {galleryImages.map((src, index) => (
                <button
                  key={`${src}-${index}`}
                  type="button"
                  onClick={() => setCurrentImageIndex(index)}
                  className={`flex-shrink-0 w-20 h-16 sm:w-24 sm:h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    index === currentImageIndex
                      ? 'border-slate-900 shadow-md'
                      : 'border-transparent opacity-80 hover:opacity-100'
                  }`}
                >
                  <Image src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2 space-y-5">
              {/* Overview card */}
              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
                  <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
                      {property.title}
                    </h1>
                    <p className="text-slate-500 mt-2 flex items-start gap-1.5">
                      <Icon name="MapPin" size={16} className="mt-0.5 flex-shrink-0" />
                      {locationLine}
                    </p>
                  </div>
                  <div className="sm:text-right">
                    <p className="text-2xl sm:text-3xl font-bold text-slate-900">
                      {formatPrice(property.price, 'UGX')}
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      {typeBadge}
                      <span className="text-slate-400"> · shown in {currency}</span>
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {bedrooms != null && bedrooms !== '' && (
                    <Stat icon="BedDouble" label="Bedrooms" value={bedrooms} />
                  )}
                  {bathrooms != null && bathrooms !== '' && (
                    <Stat icon="Bath" label="Bathrooms" value={bathrooms} />
                  )}
                  {area != null && area !== '' && (
                    <Stat icon="Maximize2" label="Area" value={area} />
                  )}
                  <Stat
                    icon="Eye"
                    label="Views"
                    value={property.total_view ?? property.total_views ?? '—'}
                  />
                </div>

                <div className="mt-5 flex flex-wrap gap-2 text-xs text-slate-500">
                  {property.post_created && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100">
                      <Icon name="Clock" size={12} />
                      Listed {property.post_created}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${statusBadgeClass}`}
                  >
                    {statusText}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100">
                    ID #{property.id}
                  </span>
                </div>
              </section>

              {property.description && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-3">About this property</h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                    {property.description}
                  </p>
                </section>
              )}

              <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h2 className="text-lg font-semibold text-slate-900 mb-5">Property details</h2>
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
                  <DetailRow label="Category" value={property.category?.category} />
                  <DetailRow label="Type" value={typeBadge} />
                  <DetailRow label="Status" value={statusText} />
                  <DetailRow label="City" value={property.city} />
                  <DetailRow label="State / region" value={property.state} />
                  <DetailRow label="Country" value={property.country} />
                  <DetailRow label="Address" value={property.address} />
                  {property.parameters?.map((param) => (
                    <DetailRow key={param.id || param.name} label={param.name} value={param.value} />
                  ))}
                </div>
              </section>

              {property.assign_facilities?.length > 0 && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">Nearby facilities</h2>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {property.assign_facilities.map((facility) => (
                      <div
                        key={facility.id}
                        className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100"
                      >
                        <div className="w-11 h-11 rounded-lg overflow-hidden bg-white flex-shrink-0">
                          {facility.image ? (
                            <Image
                              src={facility.image}
                              alt={facility.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon name="MapPin" size={16} className="text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 text-sm truncate">
                            {facility.name}
                          </p>
                          {facility.distance && (
                            <p className="text-xs text-slate-500">{facility.distance} away</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {property.latitude && property.longitude && (
                <section className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                  <h2 className="text-lg font-semibold text-slate-900 mb-4">Location map</h2>
                  <div className="w-full h-64 sm:h-80 rounded-xl overflow-hidden bg-slate-100 border border-slate-100">
                    <iframe
                      src={`https://maps.google.com/maps?q=${property.latitude},${property.longitude}&z=15&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={`Map of ${property.title}`}
                    />
                  </div>
                </section>
              )}
            </div>

            {/* Sticky sidebar */}
            <aside className="lg:col-span-1 space-y-4 lg:sticky lg:top-24 self-start">
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-4">
                  Contact listing agent
                </p>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center font-bold text-lg">
                    {(property.customer_name || 'A').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-900 truncate">
                      {property.customer_name || 'Property contact'}
                    </p>
                    <p className="text-xs text-slate-500">Listed on HireBroker</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button className="w-full" onClick={() => setShowContactForm(true)}>
                    <Icon name="MessageSquare" size={16} className="mr-2" />
                    Send inquiry
                  </Button>
                  {property.mobile && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => window.open(`tel:${property.mobile}`)}
                    >
                      <Icon name="Phone" size={16} className="mr-2" />
                      Call {property.mobile}
                    </Button>
                  )}
                  {property.email && (
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() =>
                        (window.location.href = `mailto:${property.email}?subject=${encodeURIComponent(
                          `Inquiry about ${property.title}`
                        )}`)
                      }
                    >
                      <Icon name="Mail" size={16} className="mr-2" />
                      Email agent
                    </Button>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3">
                  Quick actions
                </p>
                <Button
                  variant={isSaved ? 'default' : 'outline'}
                  className="w-full"
                  onClick={handleSaveProperty}
                  disabled={addingFav || removingFav}
                >
                  <Icon name="Heart" size={16} className="mr-2" />
                  {isSaved ? 'Saved' : 'Save property'}
                </Button>
                <Button variant="outline" className="w-full" onClick={handleShare}>
                  <Icon name="Share2" size={16} className="mr-2" />
                  Share listing
                </Button>
                <Link
                  to="/notice-board"
                  className="flex items-center justify-center gap-2 w-full h-10 px-4 rounded-md text-sm font-medium border border-input bg-background hover:bg-accent transition-colors"
                >
                  <Icon name="ClipboardList" size={16} />
                  Specific Property Request
                </Link>
                <Link
                  to="/property-search-browse"
                  className="flex items-center justify-center gap-2 w-full h-10 px-4 rounded-md text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-colors"
                >
                  <Icon name="ArrowLeft" size={16} />
                  Back to search
                </Link>
              </div>

              <div className="rounded-2xl bg-slate-900 text-white p-5">
                <p className="text-[10px] uppercase tracking-widest text-sky-400 font-semibold mb-2">
                  Safety tip
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Prefer verified agents and avoid paying deposits outside HireBroker workflows.
                </p>
              </div>
            </aside>
          </div>
        </div>

        {showContactForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl border border-slate-200">
              <div className="flex items-center justify-between p-5 border-b border-slate-100">
                <h3 className="text-lg font-semibold text-slate-900">Contact agent</h3>
                <button
                  type="button"
                  onClick={() => setShowContactForm(false)}
                  className="p-2 rounded-lg hover:bg-slate-50 text-slate-500"
                >
                  <Icon name="X" size={18} />
                </button>
              </div>
              <form onSubmit={handleContactSubmit} className="p-5 space-y-4">
                <Field
                  label="Your name *"
                  value={contactForm.name}
                  onChange={(v) => setContactForm((p) => ({ ...p, name: v }))}
                  required
                />
                <Field
                  label="Email *"
                  type="email"
                  value={contactForm.email}
                  onChange={(v) => setContactForm((p) => ({ ...p, email: v }))}
                  required
                />
                <Field
                  label="Phone"
                  type="tel"
                  value={contactForm.phone}
                  onChange={(v) => setContactForm((p) => ({ ...p, phone: v }))}
                  placeholder="+256 …"
                />
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">
                    Message *
                  </label>
                  <textarea
                    value={contactForm.message}
                    onChange={(e) =>
                      setContactForm((p) => ({ ...p, message: e.target.value }))
                    }
                    rows={4}
                    required
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                    placeholder={`I'm interested in ${property.title}…`}
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button type="submit" className="flex-1">
                    Send inquiry
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowContactForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="rounded-xl bg-slate-50 border border-slate-100 px-3 py-3 text-center">
    <Icon name={icon} size={16} className="mx-auto text-slate-500 mb-1" />
    <p className="text-sm font-bold text-slate-900">{value}</p>
    <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
  </div>
);

const DetailRow = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="flex justify-between gap-4 py-2.5 border-b border-slate-100 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900 text-right">{value}</span>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', required, placeholder }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
    />
  </div>
);

export default PropertyDetail;
